import { memo } from "react";
import { Grid, TextField, InputLabel, Autocomplete } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parse } from "date-fns";
import useEntitlements from "@/auth/useEntitlements";
import ApiAutocomplete from "@/common/ApiAutocomplete";
import { SectionTitle } from "@/common/typography";

import {
    FEATURE_PROJECT_OWNER,
    FEATURE_PROJECT_TYPE,
    FEATURE_MULTI_LEGAL_ENTITY,
    FEATURE_MULTI_PERSON,
    FEATURE_CLIENT_OWNER,
} from "@/constants";
import { currencies } from "@/constants.js";

const ProjectDetailsSection = memo(function ProjectDetailsSection({
    project,
    updatePermission,
    errors,
    handleInputChange,
    cacheProjectField,
    saveProjectField,
    handleAutocompleteChange,
    handleCalendarChange,
    handleTeamMemberChange,
}) {
    const { hasFeature } = useEntitlements();

    return (
        <>
            <SectionTitle>Details</SectionTitle>

            <Grid container spacing={2} sx={{ marginBottom: "48px" }}>
                <Grid item xs={12} md={12}>
                    <TextField
                        placeholder="Project Summary"
                        value={project.description || ""}
                        onChange={handleInputChange("description")}
                        onFocus={cacheProjectField("description")}
                        onBlur={saveProjectField("description")}
                        fullWidth
                        multiline
                        rows={2}
                        InputProps={{
                            readOnly: !updatePermission,
                        }}
                    />
                </Grid>

                <Grid item xs={6} md={4}>
                    <InputLabel>Client</InputLabel>
                    <ApiAutocomplete
                        value={project.client}
                        apiRoute={"clients"}
                        fields={["name"]}
                        isOptionEqualToValue={(option, value) =>
                            option.id == value.id
                        }
                        onChange={handleAutocompleteChange(
                            "client",
                            "client_id",
                        )}
                        error={errors["client_id"]}
                        readOnly={!updatePermission}
                        fullOptionData
                        blurOnSelect
                    />
                </Grid>
                {hasFeature(FEATURE_CLIENT_OWNER) && (
                    <Grid item xs={6} md={4}>
                        <InputLabel>Client Owner (EP)</InputLabel>
                        <TextField
                            fullWidth
                            disabled
                            value={
                                project.client.owner
                                    ? `${project.client.owner.first_name} ${project.client.owner.last_name}`
                                    : ""
                            }
                        />
                    </Grid>
                )}
                {hasFeature(FEATURE_PROJECT_OWNER) && (
                    <Grid item xs={6} md={4}>
                        <InputLabel>Producer</InputLabel>
                        <ApiAutocomplete
                            readOnly={!updatePermission}
                            value={project.owner}
                            apiRoute={"persons"}
                            fields={["first_name", "last_name"]}
                            isOptionEqualToValue={(option, value) =>
                                option.id == value.id
                            }
                            onChange={handleAutocompleteChange(
                                "owner",
                                "owner_id",
                            )}
                            error={errors["owner_id"]}
                            fullOptionData
                            blurOnSelect
                        />
                    </Grid>
                )}
                {hasFeature(FEATURE_PROJECT_TYPE) && (
                    <Grid item xs={6} md={4}>
                        <InputLabel>Project Type</InputLabel>
                        {project?.project_type?.archived_at ? (
                            <TextField
                                value={project?.project_type.name || ""}
                                fullWidth
                                readOnly
                            />
                        ) : (
                            <ApiAutocomplete
                                readOnly={!updatePermission}
                                value={project.project_type}
                                apiRoute={"project-types"}
                                fields={["name"]}
                                isOptionEqualToValue={(option, value) =>
                                    option.id == value.id
                                }
                                onChange={handleAutocompleteChange(
                                    "project_type_id",
                                    "project_type_id",
                                )}
                                error={errors["project_type_id"]}
                                fullOptionData
                                blurOnSelect
                            />
                        )}
                    </Grid>
                )}
                {hasFeature(FEATURE_MULTI_LEGAL_ENTITY) && (
                    <Grid item xs={6} md={4}>
                        <InputLabel>Entity</InputLabel>
                        <ApiAutocomplete
                            readOnly={!updatePermission}
                            value={project.legal_entity}
                            apiRoute={"legal-entities"}
                            isOptionEqualToValue={(option, value) =>
                                option.id == value.id
                            }
                            fields={["name"]}
                            onChange={handleAutocompleteChange(
                                "legal_entity",
                                "legal_entity_id",
                            )}
                            error={errors["legal_entity_id"]}
                            fullOptionData
                            blurOnSelect
                        />
                    </Grid>
                )}
                <Grid item xs={6} md={4}>
                    <InputLabel>Project Currency</InputLabel>
                    <Autocomplete
                        readOnly={!updatePermission}
                        value={project.currency}
                        disablePortal
                        options={currencies}
                        renderInput={(params) => <TextField {...params} />}
                        onChange={handleAutocompleteChange("currency")}
                        blurOnSelect
                    />
                </Grid>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid item xs={6} md={4}>
                        <InputLabel>Start Date</InputLabel>
                        <DatePicker
                            readOnly={!updatePermission}
                            value={
                                project.start_date
                                    ? parse(
                                          project.start_date,
                                          "y-MM-dd",
                                          new Date(),
                                      )
                                    : null
                            }
                            inputFormat={"yyyy-MM-dd"}
                            onChange={handleCalendarChange("start_date", false)}
                            onAccept={handleCalendarChange("start_date", true)}
                            renderInput={(params) => (
                                <TextField
                                    fullWidth
                                    {...params}
                                    error={"start_date" in errors}
                                    helperText={errors["start_date"]}
                                    onFocus={cacheProjectField("start_date")}
                                    onBlur={saveProjectField("start_date")}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <InputLabel>End Date</InputLabel>
                        <DatePicker
                            readOnly={!updatePermission}
                            value={
                                project.end_date
                                    ? parse(
                                          project.end_date,
                                          "y-MM-dd",
                                          new Date(),
                                      )
                                    : null
                            }
                            inputFormat={"yyyy-MM-dd"}
                            onChange={handleCalendarChange("end_date", false)}
                            onAccept={handleCalendarChange("end_date", true)}
                            renderInput={(params) => (
                                <TextField
                                    fullWidth
                                    {...params}
                                    error={"end_date" in errors}
                                    helperText={errors["end_date"]}
                                    onFocus={cacheProjectField("end_date")}
                                    onBlur={saveProjectField("end_date")}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} md={4}>
                        <InputLabel>Live Date</InputLabel>
                        <DatePicker
                            slotProps={{
                                field: { openPickerButtonPosition: "start" },
                            }}
                            readOnly={!updatePermission}
                            value={
                                project.live_date
                                    ? parse(
                                          project.live_date,
                                          "y-MM-dd",
                                          new Date(),
                                      )
                                    : null
                            }
                            inputFormat={"yyyy-MM-dd"}
                            onChange={handleCalendarChange("live_date", false)}
                            onAccept={handleCalendarChange("live_date", true)}
                            renderInput={(params) => (
                                <TextField
                                    fullWidth
                                    {...params}
                                    error={"live_date" in errors}
                                    helperText={errors["live_date"]}
                                    onFocus={cacheProjectField("live_date")}
                                    onBlur={saveProjectField("live_date")}
                                />
                            )}
                        />
                    </Grid>
                </LocalizationProvider>
                {hasFeature(FEATURE_MULTI_PERSON) && (
                    <Grid item xs={6} md={12}>
                        <InputLabel>Team Members</InputLabel>
                        <ApiAutocomplete
                            readOnly={!updatePermission}
                            value={project.persons ? project.persons : []}
                            apiRoute={"persons"}
                            fields={["first_name", "last_name"]}
                            isOptionEqualToValue={(option, value) =>
                                option.id == value.id
                            }
                            onChange={handleTeamMemberChange}
                            error={errors["person_ids"]}
                            multiple
                            fullOptionData
                        />
                    </Grid>
                )}
            </Grid>
        </>
    );
});

ProjectDetailsSection.displayName = "ProjectDetailsSection";

export default ProjectDetailsSection;
